import { Line } from '../mathObjects/Line';
import { viewportXToAncestor } from '../mathObjects/MathObject';
import { Text, buildText } from '../mathObjects/Text';
import { injectLineContents } from '../mathObjects/utils/toLine';
import type { ProblemJSDependencies } from '../problemJSDependencies';
import { BasicObject } from '../uiObjects/basicObject';

const DIV_DECORATOR = 'div';
const DIVISION_BAR_HEIGHT = 1;
const DIVISION_BAR_RISE = 3;
const DIVISION_BAR_XTRA_WIDTH = 3;

export interface ModifierDTO {
  xml: string;
}

export class Modifier extends BasicObject {
  problemJS: ProblemJSDependencies;

  searchedForDecorator = false;
  builtDecorator = false;
  decorator?: string;

  line: Line;

  styleReference?: Text;

  constructor(problemJS: ProblemJSDependencies, line: Line) {
    super();
    this.problemJS = problemJS;
    this.gmmName = 'verticalSolvingModifier';
    this.line = line;
    this.add(line);
  }

  /**
   * @param parent tagged with 'id' that matches this modifiers 'parentId'
   * @param parentLine Line that the parent belongs to
   */
  setParent(parent: Parent, parentLine: Line): void {
    // parent's x in relation to its Line
    const parentX = viewportXToAncestor(parent, parentLine);

    this.build(parent);

    if (this.isDivisionDecorator()) {
      this.positionsForDivision(parent, parentLine);
    } else {
      this.positionForGeneric(parentX, parent, parentLine);
    }

    this.viewportY = parentLine.viewportH;
  }

  private build(parent: Parent): void {
    if (!this.styleReference) {
      this.styleReference = this.line.multi
        .getValueChildren()
        .find(c => c instanceof Text) as Text | undefined;
    }

    this.getDecoratorString();
    this.buildDecoratorObject(parent);
  }

  private getDecoratorString(): string | undefined {
    if (!this.searchedForDecorator) {
      this.decorator = this.findDecorator();
      this.searchedForDecorator = true;
    }

    return this.decorator;
  }

  // Java sends decorator in span tag, so search through multi, multi's children, etc,
  // until we find an objectFormat with decorator
  private findDecorator(): string | undefined {
    const stack: BasicObject[] = [this.line.multi];

    while (stack.length > 0) {
      const current = stack.pop();

      if (!current) continue;

      if (current.objectFormat?.decorator) {
        return current.objectFormat.decorator;
      }

      for (const child of current.children) {
        stack.push(child);
      }
    }

    return undefined;
  }

  private buildDecoratorObject(parent: Parent): void {
    if (this.decorator && !this.builtDecorator) {
      this.builtDecorator = true;

      if (this.isDivisionDecorator()) {
        this.buildDivisionDecorator(parent);
      } else {
        this.buildGenericDecorator();
      }
    }
  }

  private isDivisionDecorator(): boolean {
    return this.getDecoratorString() === DIV_DECORATOR;
  }

  private buildDivisionDecorator(parent: Parent): void {
    // IMPORTANT:
    // Unlike buildGenericDecorator(), we must explicitly size/layout the divisor line
    // before trusting this.line.viewportW (fractions and other constructs can otherwise
    // report stale/undersized widths, leading to overlap).
    this.line.buildSizeRecursive();
    this.line.multi.layoutChildrenLR();
    this.line.multi.sizeMeToFitChildren();
    this.line.buildSize();

    const bar = new BasicObject();

    bar.gmmName = 'divisionBar';
    let w = Math.max(parent.viewportW, this.line.viewportW);

    w += 2 * DIVISION_BAR_XTRA_WIDTH;
    bar.viewportW = w;
    bar.viewportH = DIVISION_BAR_HEIGHT;
    bar.viewportY = -DIVISION_BAR_RISE;
    bar.fill = 'black';
    this.add(bar);

    // position this line centered under division bar
    this.line.viewportX = (w - this.line.viewportW) / 2;

    this.viewportW = w;
    this.viewportH = this.line.viewportH;
  }

  private buildGenericDecorator(): void {
    const decoratorText = buildText(this.problemJS, 0, 0, this.decorator + ' ');
    const vertToBaseLine = this.line.getVertToBaseline();

    decoratorText.viewportY +=
      vertToBaseLine - decoratorText.getVertToBaseline();

    this.line.multi.add(decoratorText, 0);
    this.line.multi.layoutChildrenLR();
    this.line.multi.sizeMeToFitChildren();
    this.line.buildSize();
    this.sizeMeToFitChildren();

    if (this.styleReference) {
      decoratorText.objectFormat = this.styleReference.objectFormat;
    }
  }

  private positionsForDivision(parent: Parent, parentLine: Line): void {
    const originalParentX = parent.viewportX;

    // The division modifier already has perfect width because it was based on max widths of
    // parent and divisor (this.line). However, parent may be too narrow, meaning
    // its siblings to the right may overlap the division bar.
    const desiredParentW = this.viewportW - DIVISION_BAR_XTRA_WIDTH * 2;

    if (parent.viewportW < desiredParentW) {
      const increaseW = desiredParentW - parent.viewportW;

      parent.incrementViewportX(increaseW / 2);
      parent.setViewportW(desiredParentW);
      parent.slideSiblings(increaseW, parentLine, false);
    }

    this.viewportX = originalParentX;
    parent.slideSiblings(DIVISION_BAR_XTRA_WIDTH, parentLine, true);
  }

  private positionForGeneric(
    parentX: number,
    parent: Parent,
    parentLine: Line
  ): void {
    // align right side edges
    this.viewportX = parentX + parent.viewportW - this.viewportW;
    this.doNotPermitOverlapMiddleSymbols(parent, parentLine);
  }

  // Generic decorator must begin at least at middle of 'middle symbol.'
  // Division bar must begin fully after middle symbol.
  private doNotPermitOverlapMiddleSymbols(
    parent: Parent,
    parentLine: Line
  ): void {
    const countOfMiddleSymbols = parentLine.countMiddleSymbols();

    for (let i = countOfMiddleSymbols; i > 0; i--) {
      if (this.doNotPermitOverlapMiddleSymbol(i, parent, parentLine)) {
        break;
      }
    }
  }

  private doNotPermitOverlapMiddleSymbol(
    whichMiddleSymbol: number,
    parent: Parent,
    parentLine: Line
  ): boolean {
    const minRightX = parentLine.pixelsToMiddleSymbol(whichMiddleSymbol);

    if (parent.viewportX > minRightX && this.viewportX < minRightX) {
      // The following ensures right alignment via slide, but doesn't look
      // good when operand is a fraction (ex: Problem164, #13)
      // parent.slideSiblings(minRightX - this.viewportX, parentLine, true);
      this.viewportX = minRightX;

      return true;
    }

    return false;
  }

  getParentId(): number {
    // We want the first object (depth-first) that has a parentId.
    const search = (obj: BasicObject): number => {
      if (obj.objectFormat?.parentId !== undefined) {
        return obj.objectFormat.parentId;
      }

      for (const child of obj.children) {
        const found = search(child);

        if (found !== undefined) return found;
      }

      throw new Error(`No parentId found in modifier ${this.line.toXML()}`);
    };

    return search(this.line.multi);
  }
}

export const buildModifier = (
  problemJS: ProblemJSDependencies,
  modifierDTO: ModifierDTO
): Modifier => {
  const line = new Line(problemJS);

  line.setEditable(false);

  injectLineContents(line, {
    problemJS,
    xml: modifierDTO.xml,
  });

  return new Modifier(problemJS, line);
};

export class Parent extends BasicObject {
  // original members from buildIdMap (may include nested objects)
  members: BasicObject[];

  // normalized members: top-level nodes whose parent is parentLine.multi
  topLevelMembers: BasicObject[];

  constructor(members: BasicObject[], topLevelMembers: BasicObject[]) {
    super();
    this.members = members;
    this.topLevelMembers = topLevelMembers;
    this.resetViewportX();
    this.resetViewportW();
  }

  resetViewportX(): void {
    // Must be computed in a single coordinate space (Line.multi children space),
    // so we use topLevelMembers.
    this.viewportX = Math.min(...this.topLevelMembers.map(m => m.viewportX));
  }

  resetViewportW(): void {
    const left = Math.min(...this.topLevelMembers.map(m => m.viewportX));
    const right = Math.max(
      ...this.topLevelMembers.map(m => m.viewportX + m.viewportW)
    );

    this.viewportX = left;
    this.viewportW = right - left;
  }

  incrementViewportX(deltaX: number): void {
    // Slide the top-level nodes; nested nodes move with their containers.
    this.topLevelMembers.forEach(m => {
      m.viewportX += deltaX;
    });
    this.resetViewportX();
  }

  setViewportW(w: number): void {
    this.viewportW = w;
  }

  slideSiblings(deltaX: number, parentLine: Line, inclusive: boolean): void {
    // We can only slide siblings reliably if we reference top-level nodes.
    const index = Math.max(
      ...this.topLevelMembers.map(m => parentLine.multi.children.indexOf(m))
    );

    for (
      let i = inclusive ? index : index + 1;
      i < parentLine.multi.children.length;
      i++
    ) {
      parentLine.multi.children[i].viewportX += deltaX;
    }
  }
}
